import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { UnderwritingOffer } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Spinner from '@divvy-web/skylab.spinner'
import TextLink from '@divvy-web/skylab.textlink'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import TilaErrorMessage from '@sputnik/components/TilaErrorMessage'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import useGetInfoForApplicationAgreements from '@sputnik/hooks/useGetInfoForApplicationAgreements'
import ApplicationAgreements from '@sputnik/pages/PostApplicationPages/ApplicationAgreements'
import BasePageLayout from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/BasePageLayout'
import { useGetApplicationDecisions } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/GetApplicationDecisions.gql'
import { useUpdateCreditLineOfferSelectedDecision } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/UpdateCreditLineOfferSelectedDecision.gql'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './style.module.scss'

export interface CreditLineOfferPageProps {
  title: React.ReactNode
  bulletPoints: React.ReactNode[]
  learnMoreLink: string
  marketingImage: {
    alt: string
    src: string
  }
  underwritingOfferType: UnderwritingOffer // used to display the correct offer details
}

const CreditLineOfferPage = ({
  title,
  bulletPoints,
  learnMoreLink,
  marketingImage,
  underwritingOfferType,
}: CreditLineOfferPageProps) => {
  const [getClassName, makeTestId] = useNameFormatter('CreditLineOfferPage')
  const navigate = useNavigate()
  const { appId } = useParams()
  const showToast = useToast()

  const [companyDivvyUuid] = useDivvyUUID(appId)

  const { data: decisionsData } = useGetApplicationDecisions({
    skip: !companyDivvyUuid,
    variables: {
      companyId: companyDivvyUuid,
    },
  })

  const decisions = decisionsData?.decisions || []
  const decision = decisions?.find((decision) => decision?.offerType === underwritingOfferType)

  const hasMultipleDecisions = decisions?.length > 1

  const { id: decisionId, offerType, approvedAmountInCents = 0 } = decision || {}

  /* start of ApplicationAgreements */
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false)

  const handleCheckboxClick = () => {
    setHasAgreedToTerms(!hasAgreedToTerms)
  }
  /* end for ApplicationAgreements */

  /*Accepting offer logic */
  const [updateCreditLineOfferSelectedDecision, { loading: isLoadingAcceptingOffer }] =
    useUpdateCreditLineOfferSelectedDecision({
      onCompleted: () => {
        logInfo('updateCreditLineOfferSelectedDecision', {
          attributes: {
            creditAppId: appId,
            result: `accepted offer for ${offerType} and sending to polling (/app/${appId}/${PATHNAME_CONSTS.ACCEPTING_OFFER})`,
          },
          eventName: 'CreditLineOfferPageSuccess',
        })

        navigate({ pathname: `/app/${appId}/${PATHNAME_CONSTS.ACCEPTING_OFFER}` })
      },
      onError: (e) => {
        logError(e, {
          attributes: {
            action: 'updateCreditLineOfferSelectedDecision',
            creditAppId: appId,
            message: e?.message,
            result: `Error accepting offer for ${offerType}`,
          },
          eventName: 'CreditLineOfferPageFailure',
        })

        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='There was an error accepting your offer.'
            id='sputnik.CreditLineOfferPage__oRIVFo'
          />,
          {
            className: 'dd-privacy-allow',
            autoHideDelay: 5000,
          },
        )
      },
    })

  const handleAcceptClick = () => {
    if (!decisionId) {
      return
    }

    updateCreditLineOfferSelectedDecision({
      variables: {
        id: decisionId,
        salesforceCreditId: appId as string,
      },
    })
  }

  const {
    isLoggedInUserAuthorizedSigner,
    authEmail,
    hasAgreedToTila,
    creditApplication,
    isLoading,
    disableAcceptOfferButton,
    tilaInfoError,
  } = useGetInfoForApplicationAgreements({
    decision,
    isLoadingAcceptingOffer,
    hasAgreedToTerms,
  })

  if (!decisions) {
    return (
      <div>
        <Spinner centered />
      </div>
    )
  }

  return (
    <BasePageLayout
      footer={
        <div className={styles[getClassName('footer-buttons')]}>
          {hasMultipleDecisions ? (
            <BasicButton
              disabled={isLoading}
              type={BASIC_BUTTON_TYPE_OUTLINED}
              onClick={() => navigate(-1)}
            >
              <FormattedMessage
                defaultMessage='Previous'
                id='sputnik.CreditLineOfferPage__JJNc3c'
              />
            </BasicButton>
          ) : (
            <GoToDashboardButton
              buttonType={BASIC_BUTTON_TYPE_OUTLINED}
              componentName={getClassName('')}
            />
          )}
          {isLoggedInUserAuthorizedSigner && (
            <BasicButton
              disabled={isLoading || disableAcceptOfferButton}
              showSpinner={isLoadingAcceptingOffer}
              onClick={handleAcceptClick}
            >
              <FormattedMessage
                defaultMessage='Accept offer'
                id='sputnik.CreditLineOfferPage__/xro5W'
              />
            </BasicButton>
          )}
        </div>
      }
      marketingImage={marketingImage}
    >
      <>
        <div className={styles[getClassName('title')]}>{title}</div>
        {approvedAmountInCents > 0 && (
          <div className={styles[getClassName('offer-amount')]}>
            <FormattedCurrency
              maximumFractionDigits={0}
              minimumFractionDigits={2}
              value={approvedAmountInCents}
            />
          </div>
        )}
        <div className={styles[getClassName('how-it-works')]}>
          <div className={styles[getClassName('how-it-works-title')]}>
            <FormattedMessage
              defaultMessage='How it works:'
              id='sputnik.CreditLineOfferPage__ON3IdR'
            />
          </div>
          <ul className={styles[getClassName('how-it-works-bullet-points')]}>
            {bulletPoints.map((point, index) => (
              <li
                key={`point${index}`}
                className={styles[getClassName('how-it-works-item')]}
                data-testid={makeTestId(`how-it-works-item-${index}`)}
              >
                {point}
              </li>
            ))}
          </ul>
        </div>
        <div className={styles[getClassName('content-footer')]}>
          <FormattedMessage
            defaultMessage='Still need more info? <link>Learn more</link>'
            id='sputnik.CreditLineOfferPage__u8yGtN'
            values={{
              link: (chunk) => (
                <TextLink
                  href={learnMoreLink}
                  target='_blank'
                >
                  {chunk}
                </TextLink>
              ),
            }}
          />
        </div>
        {tilaInfoError && <TilaErrorMessage />}
        {isLoggedInUserAuthorizedSigner && offerType && !tilaInfoError && (
          <ApplicationAgreements
            creditApplication={creditApplication}
            decisionId={decisionId}
            hasAgreedToTerms={hasAgreedToTerms}
            hasAgreedToTila={hasAgreedToTila}
            offerType={offerType}
            onCheckboxClick={handleCheckboxClick}
          />
        )}
        {!isLoggedInUserAuthorizedSigner && !tilaInfoError && (
          <div className={styles[getClassName('unauthorized-accept-message')]}>
            <FormattedMessage
              defaultMessage='Only the authorized signer ({email}) can accept this offer.'
              id='sputnik.CreditLineOfferPage__TdYnmo'
              values={{
                email: authEmail,
              }}
            />
          </div>
        )}
      </>
    </BasePageLayout>
  )
}

export default CreditLineOfferPage
